// src/utils/authUtils.ts

import type { HealthStoreState } from '$lib/types/api/responses.types';
import { isDevMode } from '../../utils/dev';
import { secureStorage } from '$lib/utils/secure-storage';

/**
 * Authentication strategy types
 */
export enum AuthStrategy {
	TOKEN = 'token',
	API_KEY = 'api_key',
	NONE = 'none'
}

/**
 * Authentication details object
 */
export interface AuthDetails {
	strategy: AuthStrategy;
	token?: string;
	tokenType?: string;
	apiKey?: string;
}

/**
 * Determine the appropriate authentication strategy based on environment, health settings, and token availability
 *
 * @param healthState Current health store state
 * @param apiKey Optional API key from environment
 * @returns AuthDetails object with strategy and credentials
 */
export function determineAuthStrategy(healthState: HealthStoreState, apiKey?: string): AuthDetails {
	// Get auth enabled status from health store
	const authEnabled = healthState.authEnabled;

	// Get environment mode
	const devMode = isDevMode();

	// Check token in secure storage
	const token = typeof window !== 'undefined' ? secureStorage.getToken() : null;
	const tokenType =
		typeof window !== 'undefined' ? secureStorage.getTokenType() || 'Bearer' : 'Bearer';
	const hasToken = !!token;

	// Check specifically for development mock token
	const isDevMockToken = token ? token.startsWith('dev-mode-mock-token-') : false;

	// DECISION LOGIC:
	// 1. If dev mode: Use API key if available when:
	//    - Auth is disabled, OR
	//    - No token exists, OR
	//    - Token is a mock dev token
	if (devMode && apiKey) {
		if (!authEnabled || !hasToken || isDevMockToken) {
			return {
				strategy: AuthStrategy.API_KEY,
				apiKey
			};
		}
	}

	// 2. If we have a real token (not dev mock token), use it
	if (hasToken && (!devMode || (devMode && !isDevMockToken))) {
		return {
			strategy: AuthStrategy.TOKEN,
			token,
			tokenType
		};
	}

	// 3. If we're in dev mode with a mock token, decide based on context
	if (devMode && hasToken && isDevMockToken) {
		if (apiKey && !authEnabled) {
			// Use API key in dev mode with auth disabled
			return {
				strategy: AuthStrategy.API_KEY,
				apiKey
			};
		} else {
			// Use mock token in dev mode with auth enabled
			return {
				strategy: AuthStrategy.TOKEN,
				token,
				tokenType
			};
		}
	}

	// 4. No auth available
	return {
		strategy: AuthStrategy.NONE
	};
}

/**
 * Get authentication headers for HTTP requests
 *
 * @param authDetails Authentication details with strategy and credentials
 * @returns HTTP headers object
 */
export function getAuthHeaders(authDetails: AuthDetails): Record<string, string> {
	const headers: Record<string, string> = {};

	switch (authDetails.strategy) {
		case AuthStrategy.TOKEN:
			if (authDetails.token) {
				headers['Authorization'] = `${authDetails.tokenType || 'Bearer'} ${authDetails.token}`;
			}
			break;

		case AuthStrategy.API_KEY:
			if (authDetails.apiKey) {
				headers['X-API-Key'] = authDetails.apiKey;
			}
			break;

		case AuthStrategy.NONE:
		default:
			// No auth headers
			break;
	}

	return headers;
}

/**
 * Helper to get the API key from environment
 *
 * @returns API key string or undefined if not available
 */
export function getApiKey(): string | undefined {
	if (typeof import.meta !== 'undefined' && import.meta.env) {
		return import.meta.env.VITE_MASTER_API_KEY;
	}
	return undefined;
}
